// ═══════════════════════════════════════════════════════════════
// AVAILABLE TOKENS — what this UI is allowed to offer, resolved live
// ═══════════════════════════════════════════════════════════════
//
// Three gates, in order. A token has to clear all of them.
//
//   1. The bridge contract      GET /api/v1/tokens returns only mints the
//                               DecentralChain contract has registered AND
//                               enabled. If it cannot mint against a
//                               deposit, the deposit is a loss.
//   2. The build's allowlist    VITE_ENABLED_TOKENS, see ../config/launch.
//   3. Display metadata         ../config/tokens, which never adds a token,
//                               only decides how one is drawn.
//
// Native SOL is the exception, and deliberately. It does not go through the
// contract's SPL registry at all: it uses the Solana program's own lock
// instruction, the one deposit path with a withdrawal that works and has
// been proven on mainnet with real funds. So SOL is offered whenever the
// allowlist permits it, registry or no registry.
//
// EVERY failure here resolves downward. An unreachable API, a 503, a
// malformed payload, a timeout — all of them leave the UI offering SOL and
// nothing else. Falling back to the full catalogue would advertise deposits
// the bridge cannot return, which is the one outcome worth engineering
// against.

import { bridgeApi } from './api';
import {
  NATIVE_SOL,
  TOKEN_CATALOGUE,
  describeRegisteredMint,
  type BridgeToken,
} from '../config/tokens';
import { isSymbolAllowed } from '../config/launch';

export interface AvailableTokens {
  /** Everything selectable, native SOL first. May be empty. */
  tokens: BridgeToken[];
  /**
   * Whether the bridge contract's token list was actually read. False means
   * `tokens` is the SOL-only fallback, not a real answer about SPL support.
   */
  registryReachable: boolean;
  /** Symbols the contract has enabled that this build's allowlist hides. */
  withheldByAllowlist: string[];
}

/** Shape of one entry in the GET /api/v1/tokens payload. */
interface RegisteredTokenResponse {
  splMint?: unknown;
  name?: unknown;
  solDecimals?: unknown;
  dccDecimals?: unknown;
  enabled?: unknown;
}

/** SOL alone: the answer whenever the contract cannot be asked. */
function solOnly(registryReachable: boolean): AvailableTokens {
  return {
    tokens: isSymbolAllowed(NATIVE_SOL.symbol) ? [NATIVE_SOL] : [],
    registryReachable,
    withheldByAllowlist: [],
  };
}

function isUsableEntry(entry: RegisteredTokenResponse): boolean {
  return (
    typeof entry?.splMint === 'string' &&
    entry.splMint.length > 0 &&
    typeof entry.name === 'string' &&
    entry.name.length > 0 &&
    // The endpoint filters to enabled already. Re-check rather than trust it:
    // an `enabled: false` slipping through would advertise a dead deposit.
    entry.enabled !== false
  );
}

function toBridgeToken(entry: RegisteredTokenResponse): BridgeToken {
  const splMint = entry.splMint as string;
  const catalogued = TOKEN_CATALOGUE.find((t) => t.splMint === splMint);
  if (catalogued) return catalogued;

  return describeRegisteredMint({
    splMint,
    name: entry.name as string,
    solDecimals: Number(entry.solDecimals) || 0,
    dccDecimals: Number(entry.dccDecimals) || 0,
  });
}

async function resolve(): Promise<AvailableTokens> {
  const fallback = solOnly(false);

  let payload: { tokens?: unknown };
  try {
    payload = await bridgeApi.getTokens();
  } catch (err) {
    console.warn(
      '[tokens] Bridge contract token list unreachable; offering SOL only.',
      err,
    );
    return fallback;
  }

  if (!Array.isArray(payload?.tokens)) {
    console.warn('[tokens] Unexpected /tokens payload; offering SOL only.');
    return fallback;
  }

  const tokens: BridgeToken[] = [...fallback.tokens];
  const seen = new Set(tokens.map((t) => t.splMint));
  const withheldByAllowlist: string[] = [];

  for (const entry of payload.tokens as RegisteredTokenResponse[]) {
    if (!isUsableEntry(entry)) continue;

    const token = toBridgeToken(entry);
    if (seen.has(token.splMint)) continue;

    if (!isSymbolAllowed(token.symbol)) {
      withheldByAllowlist.push(token.symbol);
      continue;
    }

    seen.add(token.splMint);
    tokens.push(token);
  }

  return { tokens, registryReachable: true, withheldByAllowlist };
}

// One request per page, shared by every component that asks. The API caches
// the contract read for 30s of its own, so a slightly longer TTL here still
// picks up an operator enabling a token without needing a reload.
const CACHE_TTL_MS = 60_000;
let cached: { at: number; result: Promise<AvailableTokens> } | null = null;

export function loadAvailableTokens(): Promise<AvailableTokens> {
  const now = Date.now();
  if (cached && now - cached.at < CACHE_TTL_MS) return cached.result;

  const result = resolve().catch(() => solOnly(false));
  cached = { at: now, result };
  return result;
}
